// src/utils/getPrayerTimes.ts
export type PrayerMode = "shia" | "sunni";

interface PrayerTimesOptions {
  mode?: PrayerMode;
  date?: Date;
}

type ShiaTimes = { Fajr: string; Shorooq: string; Dhuhr: string; Maghrib: string; };
type SunniTimes = { Fajr: string; Shorooq: string; Dhuhr: string; Asr: string; Maghrib: string; Isha: string; };

export type PrayerTimes = ShiaTimes | SunniTimes;

export interface ShiaDay extends ShiaTimes {
  Day?: string; hijry?: string; ahkam?: string; monasabat?: string; layali?: boolean;
}
export interface SunniDay extends SunniTimes {
  Day?: string; hijry?: string; ahkam?: string; monasabat?: string; layali?: boolean;
}

type ShiaJson = { meta?: { name?: string; timezone?: string; notes?: string }; days: Record<string, ShiaDay>; };
type SunniJson = { meta?: { name?: string; timezone?: string; notes?: string }; days: Record<string, SunniDay>; };

import shiaDataJson from "../data/prayer/prayer_shia.json";
import sunniDataJson from "../data/prayer/prayer_sunni.json";

const shiaData: ShiaJson = shiaDataJson as ShiaJson;
const sunniData: SunniJson = sunniDataJson as SunniJson;

function toISODate(d: Date) {
  return d.toISOString().split("T")[0]; 
}

export function getPrayerTimes(opts: PrayerTimesOptions = {}): PrayerTimes {
  const { mode = "shia", date = new Date() } = opts;
  const today = toISODate(date);

  if (mode === "shia") {
    const day = shiaData.days[today] || shiaData.days["default"];
    if (!day) return { Fajr: "--:--", Shorooq: "--:--", Dhuhr: "--:--", Maghrib: "--:--" };
    const { Fajr, Shorooq, Dhuhr, Maghrib } = day;
    return { Fajr, Shorooq, Dhuhr, Maghrib };
  } else {
    const day = sunniData.days[today] || sunniData.days["default"];
    if (!day) return { Fajr: "--:--", Shorooq: "--:--", Dhuhr: "--:--", Asr: "--:--", Maghrib: "--:--", Isha: "--:--" };
    const { Fajr, Shorooq, Dhuhr, Asr, Maghrib, Isha } = day;
    return { Fajr, Shorooq, Dhuhr, Asr, Maghrib, Isha };
  }
}

export type PrayerDayInfo = {
  dayName?: string; hijri?: string; ahkam?: string; monasabat?: string; layali?: boolean;
  meta?: { name?: string; timezone?: string; notes?: string };
};

export async function getPrayerDay(
  opts: PrayerTimesOptions = {}
): Promise<{ times: PrayerTimes; info: PrayerDayInfo }> {
  const { mode = "shia", date = new Date() } = opts;
  const today = toISODate(date);

  if (mode === "shia") {
    const day = shiaData.days[today] || shiaData.days["default"];
    const meta = shiaData.meta || {};
    const info: PrayerDayInfo = {
      dayName: day?.Day, hijri: day?.hijry, ahkam: day?.ahkam,
      monasabat: day?.monasabat, layali: day?.layali, meta,
    };
    const times = getPrayerTimes({ mode, date });
    return { times, info };
  } else {
    const day = sunniData.days[today] || sunniData.days["default"];
    const meta = sunniData.meta || {};
    const info: PrayerDayInfo = {
      dayName: day?.Day, hijri: day?.hijry, ahkam: day?.ahkam,
      monasabat: day?.monasabat, layali: day?.layali, meta,
    };
    const times = getPrayerTimes({ mode, date });
    return { times, info };
  }
}
